/**
 * Data layer for `audit-trail-security-compliance-log`.
 * Raw `fetch()` is confined to this file per the dash contract (rule D6).
 * Matches BE shape: /api/v1/audit-trail-security-compliance-log.
 */

export type AuditSeverity = "info" | "warning" | "critical";

export const ALLOWED_SEVERITY: readonly AuditSeverity[] = [
	"info",
	"warning",
	"critical",
] as const;

export interface AuditTrailRow {
	id: string;
	tenant_id: string;
	event_id: string;
	actor_id: string;
	action: string;
	resource_type: string;
	resource_id: string | null;
	severity: AuditSeverity;
	metadata: string | null;
	created_at: number;
}

export interface ListAuditTrailResponse {
	items: AuditTrailRow[];
}

export interface ListAuditTrailParams {
	actor_id?: string;
	action?: string;
	resource_type?: string;
	severity?: AuditSeverity;
	limit?: number;
}

export interface CreateAuditTrailBody {
	event_id: string;
	actor_id: string;
	action: string;
	resource_type: string;
	resource_id?: string | null;
	severity?: AuditSeverity;
	metadata?: unknown;
}

const BASE = "/api/v1/audit-trail-security-compliance-log";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export function buildListUrl(params: ListAuditTrailParams = {}): string {
	const qs = new URLSearchParams();
	if (params.actor_id) qs.set("actor_id", params.actor_id);
	if (params.action) qs.set("action", params.action);
	if (params.resource_type) qs.set("resource_type", params.resource_type);
	if (params.severity) qs.set("severity", params.severity);
	if (params.limit && Number.isFinite(params.limit)) {
		qs.set("limit", String(params.limit));
	}
	const qstr = qs.toString();
	return qstr ? `${BASE}?${qstr}` : BASE;
}

export async function list(
	params: ListAuditTrailParams = {},
): Promise<ListAuditTrailResponse> {
	const res = await fetch(buildListUrl(params), {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListAuditTrailResponse>(res);
}

export async function create(
	body: CreateAuditTrailBody,
): Promise<{ ok: boolean; id: string; event_id: string; created_at: number }> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body ?? {}),
	});
	return parseOrThrow<{
		ok: boolean;
		id: string;
		event_id: string;
		created_at: number;
	}>(res);
}

/** Filters a client-side row set by actor + date-range + severity. */
export function filterRows(
	rows: AuditTrailRow[],
	opts: {
		actor_id?: string;
		severity?: AuditSeverity | "";
		from?: number | null;
		to?: number | null;
	},
): AuditTrailRow[] {
	const actor = opts.actor_id?.trim().toLowerCase() ?? "";
	const sev = opts.severity ?? "";
	const from = opts.from ?? null;
	const to = opts.to ?? null;
	return rows.filter((r) => {
		if (actor && !r.actor_id.toLowerCase().includes(actor)) return false;
		if (sev && r.severity !== sev) return false;
		if (from !== null && r.created_at < from) return false;
		if (to !== null && r.created_at > to) return false;
		return true;
	});
}

/** Parses a YYYY-MM-DD string as UTC epoch seconds (null if invalid/empty). */
export function parseDateInput(
	s: string | undefined | null,
	endOfDay = false,
): number | null {
	if (!s) return null;
	const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
	if (!m) return null;
	const y = Number.parseInt(m[1]!, 10);
	const mo = Number.parseInt(m[2]!, 10);
	const d = Number.parseInt(m[3]!, 10);
	if (mo < 1 || mo > 12 || d < 1 || d > 31) return null;
	const ms = Date.UTC(y, mo - 1, d, endOfDay ? 23 : 0, endOfDay ? 59 : 0, endOfDay ? 59 : 0);
	if (!Number.isFinite(ms)) return null;
	return Math.floor(ms / 1000);
}

export function formatTimestamp(epochSec: number): string {
	if (!Number.isFinite(epochSec)) return "";
	const d = new Date(epochSec * 1000);
	const pad = (n: number) => String(n).padStart(2, "0");
	return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())} ${pad(d.getUTCHours())}:${pad(d.getUTCMinutes())}:${pad(d.getUTCSeconds())}Z`;
}

export const auditTrailSecurityComplianceLogQueryKeys = {
	all: ["audit-trail-security-compliance-log"] as const,
	list: (params: ListAuditTrailParams = {}) =>
		[...auditTrailSecurityComplianceLogQueryKeys.all, "list", params] as const,
	detail: (id: string) =>
		[...auditTrailSecurityComplianceLogQueryKeys.all, id] as const,
};
